import React, { useEffect, useState } from "react";
import { Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import BadgeIcon from "@mui/icons-material/Badge";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";

import request from "../../Helpers/API";
import Swal from "sweetalert2";

// Filtra el listado de GetListadoClientesCRM por domicilio — cada cliente trae cp, idLocalidad,
// idMunicipio e idEstado (mdl_Listado_Clientes_CRM). El CP filtra contra el cliente conforme se
// va escribiendo (coincide como prefijo); Estado/Municipio/Localidad se resuelven aparte porque
// el usuario puede elegirlos sin CP.
const filtrarClientesPorZona = (clientes, filtroZona) => {
  const { cp, idestado, idmunicipio, idlocalidad } = filtroZona;
  if (!cp && !idestado && !idmunicipio && !idlocalidad) return clientes;
  return clientes.filter((c) => {
    if (cp && !String(c.cp || "").startsWith(cp)) return false;
    if (idlocalidad && String(c.idLocalidad) !== String(idlocalidad)) return false;
    if (idmunicipio && String(c.idMunicipio) !== String(idmunicipio)) return false;
    if (idestado && String(c.idEstado) !== String(idestado)) return false;
    return true;
  });
};

// Modal de búsqueda avanzada de clientes — filtra el listado real por domicilio
// (CP/Estado/Municipio/Localidad, mismo flujo que el selector de Zona en CRM_DetalleGenerales) y
// permite elegir el cliente desde aquí mismo. Al seleccionar uno se cierra y aplica la selección
// en el buscador del Dashboard.
export const CRM_ModalBusquedaAvanzada = ({ open, onClose, clientes = [], onSeleccionarCliente }) => {
  const [opcionesUbicacion, setOpcionesUbicacion] = useState({ estado: [], municipio: [] });
  const [zonaCP, setZonaCP] = useState("");
  const [zonaIdEstado, setZonaIdEstado] = useState("");
  const [zonaIdMunicipio, setZonaIdMunicipio] = useState("");
  const [zonaIdLocalidad, setZonaIdLocalidad] = useState("");
  const [localidadesZona, setLocalidadesZona] = useState([]);
  const [buscandoZona, setBuscandoZona] = useState(false);
  const [filtroLocalidadZona, setFiltroLocalidadZona] = useState("");
  const [mostrarLocalidades, setMostrarLocalidades] = useState(false);

  // Catálogo de Estado/Municipio — endpoint dedicado al filtro avanzado (mismo shape que
  // GetInfoClienteID, pero sin depender de cargar un cliente en particular)
  useEffect(() => {
    if (!open) return;
    request
      .get("Clientes/GetDDLFiltroAvanzadoClientes")
      .then((result) => {
        setOpcionesUbicacion({
          estado: result?.opciones_estado || [],
          municipio: result?.opciones_municipio || [],
        });
      })
      .catch(() => setOpcionesUbicacion({ estado: [], municipio: [] }));
  }, [open]);

  useEffect(() => {
    if (!open) {
      setZonaCP("");
      setZonaIdEstado("");
      setZonaIdMunicipio("");
      setZonaIdLocalidad("");
      setLocalidadesZona([]);
      setFiltroLocalidadZona("");
      setMostrarLocalidades(false);
    }
  }, [open]);

  const buscarZonaPorCP = () => {
    if (!zonaCP) return;
    setBuscandoZona(true);
    request
      .get(`Clientes/GetLocalidadesCRMFiltroAvanzado?codigo_postal=${zonaCP}`)
      .then((result) => {
        const localidades = result || [];
        setLocalidadesZona(localidades);

        const idmunicipio = localidades[0]?.idmunicipio || "";
        const municipioEncontrado = opcionesUbicacion.municipio.find((m) => String(m.idmunicipio) === String(idmunicipio));
        const estadoEncontrado = municipioEncontrado
          ? opcionesUbicacion.estado.find((e) => String(e.idestado) === String(municipioEncontrado.idestado))
          : null;

        setZonaIdMunicipio(idmunicipio);
        setZonaIdEstado(estadoEncontrado?.idestado || "");
        setZonaIdLocalidad("");
        setFiltroLocalidadZona("");
      })
      .catch((error) => Swal.fire("CLIENTES", JSON.stringify(error), "error"))
      .finally(() => setBuscandoZona(false));
  };

  const handleChangeEstado = (e) => {
    setZonaIdEstado(e.target.value);
    setZonaIdMunicipio("");
    setZonaIdLocalidad("");
    setLocalidadesZona([]);
    setFiltroLocalidadZona("");
  };

  const handleChangeMunicipio = (e) => {
    const idmunicipio = e.target.value;
    setZonaIdMunicipio(idmunicipio);
    setZonaIdLocalidad("");
    setFiltroLocalidadZona("");

    if (!idmunicipio) {
      setLocalidadesZona([]);
      return;
    }

    setBuscandoZona(true);
    request
      .get(`Clientes/GetLocalidadesCRMFiltroAvanzado?idmunicipio=${idmunicipio}`)
      .then((result) => setLocalidadesZona(result || []))
      .catch((error) => Swal.fire("CLIENTES", JSON.stringify(error), "error"))
      .finally(() => setBuscandoZona(false));
  };

  const handleLimpiarZona = () => {
    setZonaCP("");
    setZonaIdEstado("");
    setZonaIdMunicipio("");
    setZonaIdLocalidad("");
    setLocalidadesZona([]);
    setFiltroLocalidadZona("");
  };

  const handleSeleccionarLocalidad = (l) => {
    setZonaIdLocalidad(l.idlocalidad);
    setFiltroLocalidadZona(l.localidad);
    setMostrarLocalidades(false);
  };

  const localidadesZonaFiltradas = localidadesZona.filter((l) =>
    (l.localidad || "").toLowerCase().includes(filtroLocalidadZona.toLowerCase())
  );

  const clientesFiltrados = filtrarClientesPorZona(clientes, {
    cp: zonaCP,
    idestado: zonaIdEstado,
    idmunicipio: zonaIdMunicipio,
    idlocalidad: zonaIdLocalidad,
  });

  const handleSeleccionar = (cliente) => {
    onSeleccionarCliente(cliente);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className="crm-modal-card crm-modal-card-xl crm-modal-card-overflow-visible">
        <button type="button" className="crm-modal-cerrar" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </button>

        <h3 className="crm-modal-titulo">Búsqueda avanzada de clientes</h3>

        <div className="crm-modal-body">
          <div className="crm-busqueda-zona-grid">
            <div className="crm-field">
              <label>Código postal</label>
              <div className="crm-field-inline-btn">
                <div className="crm-field-cp-wrap">
                  <input
                    type="text"
                    className="mono crm-field-editable"
                    value={zonaCP}
                    onChange={(e) => setZonaCP(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && buscarZonaPorCP()}
                  />
                </div>
                <button type="button" className="btn-cp-buscar" title="Buscar por CP" disabled={!zonaCP || buscandoZona} onClick={buscarZonaPorCP}>
                  <SearchIcon fontSize="small" />
                </button>
              </div>
            </div>
            <div className="crm-field">
              <label>Estado</label>
              <select className="crm-field-editable" disabled={!!zonaCP} value={zonaIdEstado} onChange={handleChangeEstado}>
                <option value="">Seleccionar</option>
                {opcionesUbicacion.estado.map((es) => (
                  <option key={es.idestado} value={es.idestado}>{es.estado}</option>
                ))}
              </select>
            </div>
            <div className="crm-field">
              <label>Municipio</label>
              <select className="crm-field-editable" disabled={!!zonaCP || !zonaIdEstado} value={zonaIdMunicipio} onChange={handleChangeMunicipio}>
                <option value="">Seleccionar</option>
                {opcionesUbicacion.municipio
                  .filter((m) => String(m.idestado) === String(zonaIdEstado))
                  .map((m) => (
                    <option key={m.idmunicipio} value={m.idmunicipio}>{m.municipio}</option>
                  ))}
              </select>
            </div>
            <div className="crm-field crm-field-localidad">
              <label>Localidad</label>
              <input
                type="text"
                className="crm-field-editable"
                placeholder={localidadesZona.length > 0 ? "Buscar localidad..." : "Captura CP o Municipio"}
                value={filtroLocalidadZona}
                disabled={localidadesZona.length === 0}
                onChange={(e) => {
                  setFiltroLocalidadZona(e.target.value);
                  if (zonaIdLocalidad) setZonaIdLocalidad("");
                  setMostrarLocalidades(true);
                }}
                onFocus={() => setMostrarLocalidades(true)}
                onBlur={() => setTimeout(() => setMostrarLocalidades(false), 150)}
                autoComplete="off"
              />

              {mostrarLocalidades && localidadesZona.length > 0 && (
                <div className="crm-search-dropdown scroll" onMouseDown={(e) => e.preventDefault()}>
                  {localidadesZonaFiltradas.length === 0 ? (
                    <div className="crm-search-result crm-search-result-vacio">Sin resultados</div>
                  ) : (
                    localidadesZonaFiltradas.map((l) => (
                      <div
                        key={l.idlocalidad}
                        className="crm-search-result"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSeleccionarLocalidad(l)}
                      >
                        <div className="crm-res-info">
                          <span className="crm-res-nombre">{l.localidad}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            {(zonaCP || zonaIdEstado) && (
              <button type="button" className="btn-cp-limpiar" title="Quitar filtro de zona" onClick={handleLimpiarZona}>×</button>
            )}
          </div>

          <div className="crm-search-dropdown crm-modal-busqueda-resultados scroll">
            {clientesFiltrados.length > 0 ? (
              clientesFiltrados.map((c) => (
                <div key={c.idcliente} className="crm-search-result" onClick={() => handleSeleccionar(c)}>
                  <div className="crm-res-avatar"><AccountCircleOutlinedIcon /></div>
                  <div className="crm-res-info">
                    <span className="crm-res-nombre">{c.razon_social}</span>
                    <span className="crm-res-meta">
                      <BadgeIcon /> {c.rfc}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="crm-search-no-result">Sin clientes que coincidan con el domicilio buscado</div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
